import { getApiMethod } from '@/modules/apiSelector';
import logger from '@/modules/Logger';

const MAX_UID_RETRIES = 3;
const MAX_UID_RETRIES_CLOUD = 4;
const BASE_DELAY = 1000;
const BASE_DELAY_CLOUD = 2000;

export default {
  namespaced: true,
  state: {
    rtcClient: null,
    localUser: {
      peerId: null,
      stream: null
    },
    peerError: null,
    isShareScreenOn: false,
    chimeShareScreenDidStop: false,
    deviceRtcError: false,
    virtualBgModel: null,
    virtualBg: 'none',
    activeAudioTrack: null,
    audioOnly: false,
    measuredBandwidth: 0,

    uidRetryCount: 0
  },
  getters: {
    /**
     * Check if media devices is invalid
     *
     * @return boolean
     */
    isMediaInvalid: state => {
      if (state.peerError) {
        return state.peerError.msg === 'OverconstrainedError';
      }
      return false;
    },

    /**
     * Agora uid conflict
     */
    isUidConflict: state => {
      if (state.peerError) {
        const msg = state.peerError;
        return (msg.code && msg.code == 'UID_CONFLICT');
      }
      return false;
    },

    /**
     * UID BANNED - Simultaneous Connection
     */
    isUidBanned: state => {
      return state.peerError?.code === 'UID_BANNED';
    },

    /**
     * Measured bandwidth in kbps from DetectSpeed
     */
    measuredBandwidth: state => state.measuredBandwidth
  },
  mutations: {
    setRtcClient(state, data) {
      state.rtcClient = data;
    },
    setLocalUserStream(state, data) {
      state.localUser.peerId = data.peerId || state.localUser.peerId;
      state.localUser.stream = data.stream || state.localUser.stream;
    },
    setPeerError(state, data) {
      state.peerError = data;
    },
    setIsShareScreenOn(state, data) {
      state.isShareScreenOn = data;
    },
    setDeviceRtcError(state, data) {
      state.disconnectRtc = data;
    },
    setVirtualBg(state, data) {
      state.virtualBg = data;
    },
    setVirtualBgModel(state, data) {
      state.virtualBgModel = data;
    },
    setActiveAudioTrack(state, data) {
      state.activeAudioTrack = data;
    },
    setAudioOnly(state, data) {
      state.audioOnly = data;
    },
    setChimeShareScreenDidStop(state, data) {
      state.chimeShareScreenDidStop = data;
    },
    setUidRetryCount(state, count) {
      state.uidRetryCount = count;
    },
    setMeasuredBandwidth(state, value) {
      const parsed = parseInt(value, 10);
      if (!isNaN(parsed) && parsed >= 0) {
        state.measuredBandwidth = parsed;
      }
    }
  },
  actions: {
    /**
     * Updated Local Stream
     *
     * @param {commit} commit
     * @return void
     */
    updateLocalStream({ commit }, rtc) {
      if (!rtc.hasOwnProperty('error')) {
        commit('setLocalUserStream', rtc);
        commit('setPeerError', null);
      } else {
        commit('setPeerError', rtc.error);
        commit('setDeviceRtcError', true);
      }
    },

    /**
     * Handle UID conflict retry with exponential backoff.
     * Returns credential token for retry UID, or null if retries exhausted.
     */
    async handleUidConflictRetry({ state, commit, rootState }, retryUserId) {
      const hasRecording = rootState.enabledCloudRecording || false;
      const maxRetries = hasRecording ? MAX_UID_RETRIES_CLOUD : MAX_UID_RETRIES;
      const baseDelay = hasRecording ? BASE_DELAY_CLOUD : BASE_DELAY;
      const attempt = state.uidRetryCount;

      if (attempt > maxRetries) {
        console.log(`[UID_RETRY] ❌ Max retries exhausted (${attempt}/${maxRetries}). hasRecording: ${hasRecording}`);
        logger.error('Max UID retry attempts reached', {
          operation: 'webrtc:uid-conflict',
          totalAttempts: attempt,
          hasRecording,
          lessonId: rootState.lessonId
        });
        return null;
      }

      const delay = baseDelay * Math.pow(2, attempt - 1);
      
      console.log(`[UID_RETRY] ⏳ Exponential backoff triggered — attempt: ${attempt}/${maxRetries}, delay: ${delay}ms, baseDelay: ${baseDelay}ms, hasRecording: ${hasRecording}`);

      logger.info('UID conflict - requesting new token with backoff', {
        operation: 'webrtc:uid-conflict',
        attempt,
        delay,
        hasRecording,
        lessonId: rootState.lessonId
      });

      await new Promise(resolve => setTimeout(resolve, delay));

      console.log(`[UID_RETRY] 🔄 Backoff complete, requesting new token. uid: ${retryUserId}, student_book_id: ${rootState.lessonId}`);

      const params = {
        uid: retryUserId,
        student_book_id: rootState.lessonId,
        is_compute: true
      };

      const getAgoraToken = getApiMethod('agoraToken');
      const result = await getAgoraToken(params);

      console.log(`[UID_RETRY] ✅ Backend response:`, result);

      commit('setPeerError', null);

      return result;
    }
  }
};
